<template>
  <v-navigation-drawer
    id="inbox-drawer"
    :model-value="inbox.state.isOpen"
    location="top"
    temporary
    :width="drawerHeight"
    class="inbox-drawer"
    :class="{ 'inbox-drawer--expanded': expanded }"
    role="dialog"
    aria-modal="true"
    aria-labelledby="inbox-drawer-title"
    tabindex="-1"
    :inert="!inbox.state.isOpen"
    @update:model-value="onModelValueUpdate"
  >
    <v-toolbar class="details-title" height="56">
      <v-toolbar-title
        id="inbox-drawer-title"
        text="Messages"
      ></v-toolbar-title>
      <v-btn
        id="expand-messages-btn"
        icon
        variant="text"
        @click="expanded = !expanded"
      >
        <v-icon size="16"
          >fa-regular fa-arrow-up-right-and-arrow-down-left-from-center</v-icon
        >
        <span class="sr-only">
          {{ expanded ? "Collapse" : "Expand" }} messages
        </span>
      </v-btn>
      <v-btn id="close-messages-btn" icon variant="text" @click="close">
        <v-icon size="18">fa-regular fa-xmark</v-icon>
        <span class="sr-only">Close messages</span>
      </v-btn>
    </v-toolbar>

    <div class="inbox-drawer__body">
      <div class="inbox-drawer__list">
        <ConversationList v-if="inbox.state.isOpen" />
      </div>

      <div class="inbox-drawer__detail">
        <ThreadView v-if="inbox.state.rightPane === 'thread'" />
        <NewMessagePanel v-else-if="inbox.state.rightPane === 'picker'" />
        <!-- ComposeFirstMessagePanel lands here for rightPane === 'compose'. -->
        <div v-else class="pa-4">{{ inbox.state.rightPane }}</div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import useInboxState from "@/composables/useInboxState";
import ConversationList from "@/components/messaging/ConversationList.vue";
import ThreadView from "@/components/messaging/ThreadView.vue";
import NewMessagePanel from "@/components/messaging/NewMessagePanel.vue";

const inbox = useInboxState();

export default {
  name: "InboxDrawer",
  components: { ConversationList, ThreadView, NewMessagePanel },
  data: () => ({
    inbox,
    expanded: false
  }),
  computed: {
    drawerHeight: function() {
      // Nav-drawer maps the `width` prop to the element's HEIGHT, and the hide transform uses
      // that same number - so full height has to come from here and never from CSS.
      return this.$vuetify.display.height;
    }
  },
  watch: {
    "inbox.state.isOpen": function(open) {
      // Reopen collapsed rather than remembering the expanded width
      if (!open) {
        this.expanded = false;
      }
      // ADA fix for no Escape handling or focus management on navigation drawer
      if (open) {
        window.addEventListener("keydown", this.onKeydown);
        this.moveFocusIn();
      } else {
        window.removeEventListener("keydown", this.onKeydown);
        this.restoreFocus();
      }
    }
  },
  beforeUnmount: function() {
    window.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown: function(event) {
      if (event.key === "Escape") {
        this.close();
      } else if (event.key === "Tab") {
        this.trapFocus(event);
      }
    },
    drawerEl: function() {
      return document.getElementById("inbox-drawer");
    },
    moveFocusIn: function() {
      // Remember the trigger so closing can hand focus back to it
      this.returnFocusEl = document.activeElement;
      this.$nextTick(() => {
        const drawer = this.drawerEl();
        if (drawer && !drawer.contains(document.activeElement)) {
          drawer.focus();
        }
      });
    },
    restoreFocus: function() {
      const target = this.returnFocusEl;
      this.returnFocusEl = null;
      // A legacy entry point can unmount while the drawer is open - fall back to the launcher
      const fallback = document.getElementById("messages-launcher");
      if (target && target.isConnected && target !== document.body) {
        target.focus();
      } else if (fallback) {
        fallback.focus();
      }
    },
    trapFocus: function(event) {
      const drawer = this.drawerEl();
      if (!drawer) return;

      const focusable = Array.from(
        drawer.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.disabled && el.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        drawer.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !drawer.contains(active);

      if (
        event.shiftKey &&
        (active === first || active === drawer || outside)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    },
    close: function() {
      inbox.close();
    },
    onModelValueUpdate: function(value) {
      // emit this on a backdrop click too, not just our own buttons
      if (!value) inbox.close();
    }
  }
};
</script>

<style lang="scss" scoped>
.inbox-drawer {
  outline: none; // ADA fix
  width: 50% !important;
  left: auto !important;
  right: 0 !important;

  &.inbox-drawer--expanded {
    width: 100% !important;
  }
}

.details-title {
  @include details-title-bar;
  border-radius: 0 !important;
  height: 56px;

  :deep(.v-toolbar-title) {
    @include details-title-bar-text;
  }
}

.inbox-drawer__body {
  display: flex;
  height: calc(100% - 56px); // toolbar height
}

.inbox-drawer__list {
  width: 320px;
  flex-shrink: 0;
  overflow-y: auto;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.inbox-drawer__detail {
  flex: 1;
  overflow-y: auto;
}
</style>
